'use memo';

import { ContentLayout, PageLayout } from 'layout';
import { AgxToast, cleanFullAddress, PropertyAddress } from '@urbanx/agx-ui-components';
import { ReactElement, useContext, useEffect, useMemo, useState } from 'react';
import { MenuTitles, Pages } from 'constants/menu';
import { useSelectedAgency, useSelectedCampaign, useSelectedContract } from 'hooks';
import { AuthContext } from '@urbanx/agx.auth.client';
import {
  AgxColumn,
  AgxDivider,
  AgxRow,
  AgxSaveChangesButton,
  AgxTypography,
  SAVE_BUTTON_STATES,
} from '@urbanx/agx.ui-components2';
import { FormPrompt } from 'components/FormPrompt';
import './ContractSubPage.scss';
import { AgxToastState } from 'types/commonTypes';
import LeftSubmenu, { SubPage } from 'components/left-submenu/LeftSubmenu';

interface ContractSubPageProps {
  pageTitle: string;
  secondaryActiveMenu: MenuTitles;
  currentPage: Pages;
  subPages: SubPage[];
  onSaveChanges: (success: () => void) => void;
  children: React.ReactNode;
  isFormDirty: boolean;
  isSaving: boolean;
  isError: boolean;
  hideSaveButton?: boolean;
  secondaryActions?: ReactElement;
}

export const ContractSubPage = ({
  pageTitle,
  secondaryActiveMenu,
  currentPage,
  subPages,
  onSaveChanges,
  children,
  isFormDirty,
  isSaving,
  isError,
  hideSaveButton = false,
  secondaryActions,
}: ContractSubPageProps) => {
  const { userAccount } = useContext(AuthContext);
  const { agency } = useSelectedAgency();
  const { campaign } = useSelectedCampaign();
  const { contract } = useSelectedContract();
  const [hasSaved, setHasSaved] = useState(false);
  const [toastState, updateToastState] = useState<AgxToastState>({
    color: 'success',
    message: '',
    open: false,
  });

  const savingState = useMemo(() => {
    if (isSaving) return SAVE_BUTTON_STATES.SAVING;
    if (hasSaved) return SAVE_BUTTON_STATES.SAVED;
    if (isFormDirty) return SAVE_BUTTON_STATES.UNSAVED;
    if (isError) return SAVE_BUTTON_STATES.ERROR;
    return SAVE_BUTTON_STATES.UNSAVED;
  }, [isSaving, isError, isFormDirty, hasSaved]);

  useEffect(() => {
    if (isFormDirty) {
      setHasSaved(false);
    }
  }, [isFormDirty]);

  useEffect(() => {
    if (savingState === SAVE_BUTTON_STATES.SAVED) {
      updateToastState({
        color: 'success',
        message: 'Changes saved successfully',
        open: true,
      });
    }
  }, [savingState]);

  if (!campaign || !contract?.buyers) return null;

  return (
    <PageLayout
      agentName={userAccount?.firstName || ''}
      primaryPageTitle={agency?.name || ''}
      currentPageTitle={`Campaigns / ${cleanFullAddress(campaign?.address as PropertyAddress)}`}
      isCampaignsSection={true}
    >
      <ContentLayout
        hasSecondaryMenu={false}
        secondaryActiveMenu={secondaryActiveMenu}
        isContracts={true}
        secondaryActions={secondaryActions}
      >
        <AgxToast selector="#agxToast" toastState={toastState} />
        <FormPrompt hasUnsavedChanges={isFormDirty} />
        <AgxColumn gap="spacing-xl" fill>
          <AgxColumn gap="sm" fill id="subPageHeadingContainer">
            <AgxRow
              id="subPageHeading"
              gap="spacing-md"
              fill
              padding={{ left: 'spacing-md', right: 'spacing-md' }}
              justifyContent="center"
              alignItems="alignCenter"
            >
              <AgxRow gap="spacing-md" fill alignItems="flex-start" justifyContent="space-between">
                <AgxTypography
                  id={`pageTitle-${pageTitle.replace(/\s+/g, '-')}`}
                  type={'body_ui'}
                  size="medium"
                >
                  {pageTitle}
                </AgxTypography>
              </AgxRow>
              {!hideSaveButton && (
                <AgxRow gap="spacing-md" fill alignItems="alignCenter" justifyContent="right">
                  <AgxSaveChangesButton
                    state={savingState}
                    id="btnSaveChanges"
                    text="Save Changes"
                    size="small"
                    variant="primary"
                    onClick={() =>
                      onSaveChanges(() => {
                        setHasSaved(true);
                      })
                    }
                    disabled={!isFormDirty}
                  />
                </AgxRow>
              )}
            </AgxRow>
            <AgxDivider variant="horizontal" pushMargin="spacing-3xl" />
          </AgxColumn>
          <AgxRow gap="spacing-md" fill>
            <LeftSubmenu currentPage={currentPage} subPages={subPages} />
            <AgxColumn gap="spacing-xl" fill>
              {children}
            </AgxColumn>
          </AgxRow>
        </AgxColumn>
      </ContentLayout>
    </PageLayout>
  );
};
